/**
 * @fileoverview Modal para seleção de produtos, categorias ou marcas.
 * @module TargetSelector
 * @requires react
 * @requires @mui/material
 * @requires ../../services/productsApi
 * @since 1.0.0
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import { getProducts, getCategories, getBrands } from '../../services/productsApi';

/**
 * Modal para seleção de targets (produtos, categorias, marcas).
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @param {Function} props.onAdd - Callback ao adicionar itens selecionados
 * @returns {JSX.Element}
 */
export default function TargetSelector({ open, onClose, onAdd }) {
  const [type, setType] = useState('products');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (open) {
      loadItems();
    }
  }, [open, type]);

  /**
   * Carrega itens baseado no tipo selecionado.
   * @async
   * @function loadItems
   * @returns {Promise<void>}
   */
  const loadItems = async () => {
    try {
      setLoading(true);
      setSelected([]);
      let data = [];
      
      if (type === 'products') {
        data = await getProducts();
      } else if (type === 'categories') {
        data = await getCategories();
      } else if (type === 'brands') {
        data = await getBrands();
      }
      
      setItems(data);
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Alterna seleção de um item.
   * @function handleToggle
   * @param {string|number} id - ID do item
   * @returns {void}
   */
  const handleToggle = (id) => {
    const currentIndex = selected.indexOf(id);
    const newSelected = [...selected];

    if (currentIndex === -1) {
      newSelected.push(id);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelected(newSelected);
  };

  /**
   * Adiciona itens selecionados.
   * @function handleAdd
   * @returns {void}
   */
  const handleAdd = () => {
    const selectedItems = items
      .filter(item => selected.includes(item.id))
      .map(item => ({
        id: item.id,
        type,
        name: item.name || item.nome
      }));
    
    onAdd(selectedItems);
    setSelected([]);
  };

  /**
   * Obtém nome do item baseado no tipo.
   * @function getItemName
   * @param {Object} item - Item
   * @returns {string} Nome do item
   */
  const getItemName = (item) => {
    return item.name || item.nome || item.descricao || '';
  };

  const paginatedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Selecionar Itens</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              label="Tipo"
            >
              <MenuItem value="products">Produtos</MenuItem>
              <MenuItem value="categories">Categorias</MenuItem>
              <MenuItem value="brands">Marcas</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Nome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.indexOf(item.id) !== -1}
                          onChange={() => handleToggle(item.id)}
                        />
                      </TableCell>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{getItemName(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={items.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página:"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button 
          onClick={handleAdd} 
          variant="contained" 
          disabled={selected.length === 0}
        >
          Adicionar Selecionados ({selected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
